import { action, computed, observable } from 'mobx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';


import callApi from '../utils/callApi';
import {
  ARHIVE_LIST_BY_ANCHOR,
  ARHIVE_LIST_BY_SEGMENT,
  FETCH_VIDEO_SEGMENT_BY_ID,
  FETCH_VIDEO_PROGRAM_BY_ID,
  SEGMENT_LIKE,
  SEGMENTS_LIST,
  PUBLISHED_PROGRAM_SEGMENTS,
  FEATURED_LIST_ALL,
} from '../constants/apiUrls/apiUrls';

import NotificationStore from './NotificationStore';


const DROPDOWN_SEGMENT_IDS = [256, 257, 18, 63, 6, 208, 54, 213, 7, 19, 35, 234, 259, 268, 11, 224, 28, 10, 206, 248, 34, 235, 33, 42, 240, 5, 262, 202, 25, 44, 201, 255, 219, 260, 227, 23];


class VideoPlayerStore {
  @observable program = undefined;
  @observable programSegments = undefined;
  @observable currentPosition = undefined;
  @observable videoPlayerRef = null;
  @observable showTranscript = false;
  @observable featuredPrograms = [];
  @observable selectedFilter = 'featured';
  @observable selectionList = [];
  @observable allSegments = [];
  @observable selectedProgramId = null;
  @observable lastPlayedProgramId = null;
  @observable hideLoadMore = false;
  @observable isLoading = false;
  @observable disableLikeButton = false;
  @observable loadMoreButtonLoading = false;
  @observable linkCopied = false;
  @observable isFeaturedClipMuteButtonEngaged = true;
  pageNum = 0;
  pageSize = 15;

  constructor() {
    this.dayjs = dayjs;
    this.dayjs.extend(utc);
    this.dayjs.extend(localizedFormat);
  }

  @action setIsFeaturedClipMuteButtonEngaged = (value) => {
    this.isFeaturedClipMuteButtonEngaged = value;
  };

  @action setSelectedProgramId = (value) => {
    if (this.selectedProgramId === value) {
      return;
    }
    this.hideLoadMore = false;
    this.selectedProgramId = value;
    this.fetchProgramList(value);
  };

  @action setLastPlayedProgramId = (id) => {
    this.lastPlayedProgramId = id;
  };

  fetchProgramList = (value, isLoadMore) => {
    let url = this.getUrlBySelectedFilter(this.selectedFilter);
    url = url.replace('{id}', value).replace('{page}', this.pageNum).replace('{size}', this.pageSize);
    callApi(url, 'get', {}, {}, {}, true, true).then((res) => {
      if (res.data.segments?.length < this.pageSize) {
        this.hideLoadMore = true;
      }
      if (isLoadMore) {
        this.loadMoreButtonLoading = false;
        this.featuredPrograms = [...this.featuredPrograms, ...this.formatDataObject(res.data.segments)];
      } else {
        this.featuredPrograms = this.formatDataObject(res.data.segments);
      }
    });
  };

  @action handleTranscript = () => {
    if (this.programSegments?.[this.currentPosition]?.description !== '') {
      this.showTranscript = !this.showTranscript;
    }
  };

  getDayOfWeek = (date) => {
    const dayNum = this.dayjs(date).utc().day();

    switch (dayNum) {
      case 0:
        return 'Sunday';
      case 1:
        return 'Monday';
      case 2:
        return 'Tuesday';
      case 3:
        return 'Wednesday';
      case 4:
        return 'Thursday';
      case 5:
        return 'Friday';
      case 6:
        return 'Saturday';
      default:
        break;
    }
  };

  truncate = (text, suffix = ' ...', maxCharLength = 110) => {
    const parser = new DOMParser();
    const parsedText = parser.parseFromString(text, 'text/html').
      documentElement.textContent;
    const shouldUseSuffix = maxCharLength < parsedText.length;

    return `${parsedText.substring(0, maxCharLength)}${shouldUseSuffix ? suffix : ''}`;
  };

  @computed get titleDate() {
    if (this.programSegments?.[this.currentPosition]) {
      const timestamp = this.program?.programSegments?.[this.currentPosition]?.publishedAt || this.program.title || this.program.programDate || this.program.publishedAt;
      const day = this.getDayOfWeek(timestamp);
      const date = this.dayjs(timestamp).utc().format('LL');
      const time = this.dayjs(timestamp).utc().format('LT');

      return `${day} ${date} ${time}`;
    }
    if (this.programSegments?.segment) {
      return (
        this.programSegments.anchors[0] &&
        `${this.programSegments.anchors[0].name} in ${this.programSegments.segment.name
        }`
      );
    }

    return '';
  }

  @action setVideoPlayerRef = (videoPlayerRef) => {
    this.videoPlayerRef = videoPlayerRef;
  };

  @action setProgramData = ({ program, programSegments, currentPosition }) => {
    this.program = program;
    this.lastPlayedProgramId = programSegments?.[currentPosition]?.programSegmentId;
    this.programSegments = programSegments;
    this.currentPosition = currentPosition;
  };

  changeVideoPlayerSrc = (newPosition) => {
    if (this.videoPlayerRef && this.videoPlayerRef.current) {
      this.videoPlayerRef.current.changeVideoSrc(this.programSegments?.[newPosition], newPosition);
      this.linkCopied = false;
    }

    if (newPosition) {
      this.currentPosition = newPosition;
    }
  };

  @action setCurrentPosition = (index) => {
    if (index >= this.programSegments?.length) {
      return;
    }

    this.currentPosition = index;
  };

  getUrlBySelectedFilter = (value) => {
    switch (value) {
      case 'featured':
        return PUBLISHED_PROGRAM_SEGMENTS;
      case 'spotlight':
        return FEATURED_LIST_ALL;
      case 'anchors':
        return ARHIVE_LIST_BY_ANCHOR;
      case 'segments':
        return ARHIVE_LIST_BY_SEGMENT;
      default:
        return PUBLISHED_PROGRAM_SEGMENTS;
    }
  };

  formatDataObject = (dataArray) => {
    const formattedDataArray = [];
    dataArray.map((data) => {
      const formattedData = {
        ...data,
        image: data?.thumbnailImageUrl || data?.image,
        bannerImage: data?.imageUrl || data?.bannerImage,
        title: data?.name || data?.title,
        clipUrl: data?.clipUrl || data?.clipPreview?.previewFileLocation,
        imageUrl: data?.thumbnailImageUrl || data?.image,
      };
      formattedDataArray.push(formattedData);
    });

    return formattedDataArray;
  };

  getFeaturedProgramData = (isLoadMore) => {
    const url = this.getUrlBySelectedFilter().
      replace('{page}', this.pageNum).
      replace('{size}', this.pageSize);
    const searchParams = {
      sortCriteria: 'pinned,title',
      sortOrder: 'DESC',
    };
    callApi(url, 'get', searchParams, {}, {}, true, true).then((res) => {
      if (res.data.segments?.length < this.pageSize) {
        this.hideLoadMore = true;
      }
      if (isLoadMore) {
        this.loadMoreButtonLoading = false;
        this.featuredPrograms = [...this.featuredPrograms, ...this.formatDataObject(res.data.segments)];
      } else {
        this.featuredPrograms = this.formatDataObject(res.data.segments);
      }
    });
  };

  fetchAndSetSegmentsList = () => {
    if (this.allSegments?.length > 1) {
      return;
    }
    callApi(SEGMENTS_LIST).then((res) => {
      const filteredList = res.data.filter((segment) => DROPDOWN_SEGMENT_IDS.includes(segment.id));
      this.allSegments = filteredList;
    }).catch();
  };

  @action handleFilterChange = (e) => {
    if (this.selectedFilter === e.target.value) {
      return;
    }

    this.selectedFilter = e.target.value;
    this.pageNum = 0;
    this.pageSize = 12;
    this.hideLoadMore = false;
    if (e.target.value === 'featured') {
      this.featuredPrograms = [];
      this.getFeaturedProgramData(false);
    } else if (e.target.value === 'spotlight') {
      this.featuredPrograms = [];
      this.getSpotlightData(false);
    }
  };

  getSpotlightData = (isLoadMore) => {
    const url = FEATURED_LIST_ALL.replace('{page}', this.pageNum).replace('{size}', this.pageSize);
    callApi(url, 'get', {}, {}, {}, true, true).then((res) => {
      if (res.data?.length < this.pageSize) {
        this.hideLoadMore = true;
      }
      if (isLoadMore) {
        this.loadMoreButtonLoading = false;
        this.featuredPrograms = [...this.featuredPrograms, ...res.data];
      } else {
        this.featuredPrograms = res.data || [];
      }
    });
  };

  fetchIndividualProgramSegment = (programId, programSegmentId) => {
    callApi(FETCH_VIDEO_PROGRAM_BY_ID.replace('{id}', programId), 'get', {}, {}, {}, true, true).then((res) => {
      const { data } = res;
      this.program = data || {};
      this.programSegments = data.programSegments?.filter((segment) => !segment?.freemium);
      this.isLoading = false;
      const currentProgramSegmentIndex = this.programSegments?.
        findIndex((segment) => segment.programSegmentId === programSegmentId);
      const newPosition = currentProgramSegmentIndex || 0;
      this.changeVideoPlayerSrc(newPosition);
      this.lastPlayedProgramId = programSegmentId;
    });
  };

  extractNumberFromUrl = (url) => {
    const regex = /(?!\/)w(\d+)(?=\?)/;
    const match = url.match(regex);
    if (match) {
      const extractedNumber = match[1];

      return parseInt(extractedNumber, 10);
    }

    return null;
  };

  getProgramFromProgramSegmentId = (segmentId) => {
    callApi(FETCH_VIDEO_SEGMENT_BY_ID.replace('{id}', segmentId), 'get', {}, {}, {}, true, true).then((res) => {
      this.fetchIndividualProgramSegment(res.data?.programId, segmentId);
    }).catch(() => {
      NotificationStore.error('Error getting segment');
      this.isLoading = false;
    });
  };

  @action handleContentChange = (program) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    let programSegmentId = program?.programSegmentId;
    this.isLoading = true;

    if (!programSegmentId) {
      programSegmentId = this.extractNumberFromUrl(program?.url);
      if (!programSegmentId) {
        window.location.href = program?.url;

        return;
      }
      this.getProgramFromProgramSegmentId(programSegmentId);

      return;
    }
    this.fetchIndividualProgramSegment(program?.programId, program?.programSegmentId);
  };

  @action loadMoreContent = () => {
    this.pageNum += + 1;
    this.loadMoreButtonLoading = true;
    if (this.selectedFilter === 'featured') {
      this.getFeaturedProgramData(true);
    } else if (this.selectedFilter === 'spotlight') {
      this.getSpotlightData(true);
    } else {
      this.fetchProgramList(this.selectedProgramId, true);
    }
  };

  @action handleLikeClick = (user, isVideoLiked) => {
    const method = isVideoLiked ? 'DELETE' : 'POST';
    const likeValue = isVideoLiked ? -1 : 1;
    const currentSegmentPosition = this.currentPosition;
    this.disableLikeButton = true;
    callApi(
      SEGMENT_LIKE,
      method,
      {},
      {
        segmentId: this.programSegments?.[this.currentPosition]?.programSegmentId,
        username: user?.user?.username,
      },
      {},
      true,
      true).
      then(() => {
        this.disableLikeButton = false;
        this.program.programSegments[currentSegmentPosition].likesCount += likeValue;
        this.program.programSegments[currentSegmentPosition].userLiked = !isVideoLiked;
      }).catch(() => {
        NotificationStore.error('Error sending like!');
        this.disableLikeButton = false;
      });
  };


  async copyTextToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      this.linkCopied = true;
    } catch (e) {
      this.linkCopied = false;
    }
  }


  @action handleShareClick = () => {
    const currentSegment = this.programSegments?.[this.currentPosition];
    const url = `https://www.nakednews.com/${currentSegment?.slug?.toLowerCase()?.replace(/[^A-Za-z0-9]+/g, '-')}-w${currentSegment?.programSegmentId}`;
    this.copyTextToClipboard(url);

  };
}

export default new VideoPlayerStore();
